import { useSuperAdminShops } from "@/api/super-admin/use-super-admin-shops";
import type { ReactNode } from "react";
import { CarrierIssuesTab } from "./carrier-issues-tab";
import { CustomerPortalTab } from "./customer-portal-tab";
import { PiiBackfillTab } from "./pii-backfill-tab";
import { PiiRepairTab } from "./pii-repair-tab";
import { ReplayCreationActionsTab } from "./replay-creation-actions-tab";
import { StorefrontEmbedTab } from "./storefront-embed-tab";
import { SuperAdminsTab } from "./super-admins-tab";

type AdminShop = NonNullable<
  ReturnType<typeof useSuperAdminShops>["data"]
>["shops"][number];

export function AdminPageShell({
  title,
  description,
  actions,
  mainClassName,
  children,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  mainClassName?: string;
  children: ReactNode;
}) {
  // Opaque backgrounds only: the sidebar wrapper behind the inset is dark, any
  // translucent layer here turns the page grey and the muted text unreadable.
  return (
    <div className="flex min-h-full flex-col bg-slate-50">
      <header className="sticky top-0 z-10 border-b bg-background">
        <div className="mx-auto flex max-w-screen-2xl items-center justify-between gap-4 px-6 py-4">
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold tracking-tight text-foreground">
              {title}
            </h1>
            {description && (
              <p className="mt-0.5 text-sm text-muted-foreground">
                {description}
              </p>
            )}
          </div>
          {actions}
        </div>
      </header>
      <main
        className={[
          "mx-auto w-full max-w-screen-2xl flex-1 space-y-6 px-6 py-6",
          mainClassName ?? "",
        ].join(" ")}
      >
        {children}
      </main>
    </div>
  );
}

/**
 * Pages that need the fleet list share the super admin overview query, so
 * moving between them hits the TanStack cache instead of refetching.
 */
function WithShops({
  title,
  children,
}: {
  title: string;
  children: (shops: AdminShop[]) => ReactNode;
}) {
  const { data, isPending, isError } = useSuperAdminShops();

  return (
    <AdminPageShell title={title}>
      {isPending ? (
        <p className="text-sm text-muted-foreground">Chargement des shops…</p>
      ) : isError || !data ? (
        <p className="text-sm text-destructive">
          Impossible de charger les shops.
        </p>
      ) : (
        children(data.shops)
      )}
    </AdminPageShell>
  );
}

export function AdminCarrierIssuesPage() {
  return (
    <WithShops title="Problèmes transporteurs">
      {(shops) => (
        <CarrierIssuesTab
          shops={shops.map((s) => ({
            id: s.id,
            name: s.name ?? s.id,
            slug: s.slug ?? "",
          }))}
        />
      )}
    </WithShops>
  );
}

export function AdminReplayCreationActionsPage() {
  return (
    <AdminPageShell title="Rejouer actions">
      <ReplayCreationActionsTab />
    </AdminPageShell>
  );
}

export function AdminCustomerPortalPage() {
  return (
    <WithShops title="Portail client">
      {(shops) => <CustomerPortalTab shops={shops} />}
    </WithShops>
  );
}

function toBackfillShops(shops: AdminShop[]) {
  return shops.map((shop) => ({
    id: shop.id,
    name: shop.name,
    totalOrders: shop.totalOrders,
    isTestShop: shop.isTestShop ?? false,
  }));
}

export function AdminPiiBackfillPage() {
  return (
    <WithShops title="Coffre RGPD">
      {(shops) => <PiiBackfillTab shops={toBackfillShops(shops)} />}
    </WithShops>
  );
}

export function AdminPiiRepairPage() {
  return (
    <WithShops title="Coffre : reprise">
      {(shops) => <PiiRepairTab shops={toBackfillShops(shops)} />}
    </WithShops>
  );
}

export function AdminStorefrontEmbedPage() {
  return (
    <WithShops title="Migration embed">
      {(shops) => (
        <StorefrontEmbedTab
          shops={shops.map((shop) => ({
            id: shop.id,
            name: shop.name,
            shopifyUrl: shop.shopifyUrl,
            isTestShop: shop.isTestShop ?? false,
            isActive: shop.isActive ?? true,
          }))}
        />
      )}
    </WithShops>
  );
}

export function AdminSuperAdminsPage() {
  return (
    <AdminPageShell title="Super admins">
      <SuperAdminsTab />
    </AdminPageShell>
  );
}
